import { z } from "zod";
import {
  CompanyEnrichmentRequestIdPublicSchema,
  EnrichedCompanyIdPublicSchema,
} from "./branded-id.schemas.js";
import {
  EnrichmentRequestAdminStatusSchema,
  EnrichmentRequestStatusHistoryRowSchema,
} from "./enrichment-request-admin-common.schemas.js";

/**
 * Admin list row for a company enrichment request. `source` stays a string
 * (format-only, like the sibling enrichment transport schemas); timestamps are
 * coerced dates serialized to ISO on the wire.
 */
export const CompanyEnrichmentRequestRowSchema = z.object({
  idPublic: CompanyEnrichmentRequestIdPublicSchema,
  status: EnrichmentRequestAdminStatusSchema,
  source: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  retryCount: z.number().int(),
  errorMessage: z.string().nullable(),
  enrichedCompanyIdPublic: EnrichedCompanyIdPublicSchema.nullable(),
  /** Identifying input value for list rows: companyName || domain || linkedInSlug */
  inputLabel: z.string().nullable(),
});

export type CompanyEnrichmentRequestRow = z.infer<
  typeof CompanyEnrichmentRequestRowSchema
>;

export const CompanyEnrichmentRequestInfoSchema = z.object({
  linkedInUrl: z.string().nullable(),
  companyName: z.string().nullable(),
  domain: z.string().nullable(),
  additionalInfo: z.string().nullable(),
});

/**
 * Admin detail view of a company enrichment request. Extends the list row with
 * the request input and status-history timeline. Raw provider payload is
 * omitted.
 */
export const CompanyEnrichmentRequestDetailViewSchema =
  CompanyEnrichmentRequestRowSchema.extend({
    externalRequestId: z.string().nullable(),
    input: CompanyEnrichmentRequestInfoSchema,
    statusHistory: z.array(EnrichmentRequestStatusHistoryRowSchema),
  });

export type CompanyEnrichmentRequestDetailView = z.infer<
  typeof CompanyEnrichmentRequestDetailViewSchema
>;

/**
 * Request body for PUT /admin/enrichment/company/trigger — a batch of company
 * enrichment request public ids to (re-)enrich. Runs the same path as
 * automatic enrichment, so each request comes back created / joined (existing
 * in-flight request) / skipped (fresh data) / ineligible.
 */
export const TriggerCompanyEnrichmentBodySchema = z.object({
  companyEnrichmentRequestIdsPublic: z
    .array(CompanyEnrichmentRequestIdPublicSchema)
    .min(1),
});

export type TriggerCompanyEnrichmentBody = z.infer<
  typeof TriggerCompanyEnrichmentBodySchema
>;

export const CompanyEnrichmentTriggerResultSchema = z.object({
  companyEnrichmentRequestIdPublic: CompanyEnrichmentRequestIdPublicSchema,
  /** The created request, the joined in-flight request, or null when skipped/ineligible with no request */
  requestIdPublic: CompanyEnrichmentRequestIdPublicSchema.nullable(),
  /** CreateEnrichmentRequestResultStatus (ENQUEUED / SKIPPED_IN_FLIGHT_REQUEST / SKIPPED_FRESH_COMPANY_DATA / INELIGIBLE / FEATURE_DISABLED) */
  status: z.string(),
});

export type CompanyEnrichmentTriggerResult = z.infer<
  typeof CompanyEnrichmentTriggerResultSchema
>;
